import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect, useSelector, shallowEqual } from 'react-redux';
import { RightsContext } from 'providers/RightsContext';
import OfferFinancingStructureContainer from '../../../../components/offer/edit/financials/financingStructure/OfferFinancingStructureContainer';
import OfferFinancialServicesContainer from './financialServices/components/OfferFinancialServicesContainer';
import OfferFeeCatalogContainer from './fee/components/OfferFeeCatalogContainer';
import CommissionsContainer from './commissions/components/CommissionsContainer';
import SubsidiesContainer from './commissions/components/SubsidiesContainer';
import OfferPaymentPlanParamsContainer from './paymentPlan/components/OfferPaymentPlanParamsContainer';
import OfferInsuranceContainer from './insurance/components/OfferInsuranceContainer';
import OfferAdditionalDataContainer from 'features/offer/edit/financials/additionalData/components/OfferAdditionalDataContainer';
import LeasingExpensesContainer from 'features/offer/edit/financials/leasingExpenses/components/LeasingExpensesContainer';
import WarrantyDataContainer from 'features/document/edit/financials/warranty/components/WarrantyDataContainer';
import { getOfferClientPartnerTypeCode, getOfferType, getCurrencyCode } from 'features/offer/edit/client/form/selectors';
import { getIsContractModification } from 'selectors/dictionariesSelectors';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { loadFinancialDataSummary } from 'features/offer/edit/financials/financialDataSummary/actionCreators';
import NoDataSummaryCard from 'components/layout/NoDataSummaryCard';
import VatFinancingContainer from './vatFinancing/VatFinancingContainer';

const OfferFinancialsContainer = ({ documentId, currencyCode, siteId, isContractModification, isFacilityFrame, actions }) => {
  const {
    financialAdditionalDataRead,
    insurancesRead,
    leasingCommissionsRead,
    offerSubsidiesRead,
    documentWarrantiesRead,
    offerIncomeSchemaRead,
    seasonalSettingsRead,
    leasingCommissionsWrite,
    offerSubsidiesWrite,
    offerIncomeSchemaWrite,
    clientInvoicingRead,
    supplierInvoicingRead
  } = useContext(RightsContext);
  const { webInsuranceIntegration, offerWithWarranties, offerWithExpenses, offerWithCommissions } = useContext(ApplicationFeaturesContext);

  const { loaded, numberOfFees, numberOfCommissions, numberOfSubsidies, hasVATFinancingDocument } = useSelector(
    state => state.currentOffer.financialDataSummary,
    shallowEqual
  );

  useEffect(() => {
    if (!loaded) actions.loadFinancialDataSummary(documentId, siteId);
  }, [actions, documentId, siteId, loaded]);

  return (
    <>
      <OfferFinancingStructureContainer documentId={documentId} siteId={siteId} webInsuranceIntegration={webInsuranceIntegration} />
      {(financialAdditionalDataRead || clientInvoicingRead || supplierInvoicingRead) && (
        <OfferAdditionalDataContainer documentId={documentId} siteId={siteId} currencyCode={currencyCode} />
      )}
      {!webInsuranceIntegration && insurancesRead && !isFacilityFrame && (
        <OfferInsuranceContainer documentId={documentId} siteId={siteId} />
      )}
      {!isFacilityFrame && <OfferFinancialServicesContainer documentId={documentId} siteId={siteId} />}
      {offerWithExpenses && !isFacilityFrame && <LeasingExpensesContainer documentId={documentId} siteId={siteId} />}
      {offerIncomeSchemaRead &&
        (!offerIncomeSchemaWrite && numberOfFees === 0 ? (
          <NoDataSummaryCard message={'OfferEdit.FeeCatalog.Title'} />
        ) : (
          <OfferFeeCatalogContainer documentId={documentId} siteId={siteId} numberOfFees={numberOfFees} />
        ))}

      {offerWithWarranties && documentWarrantiesRead && <WarrantyDataContainer documentId={documentId} siteId={siteId} />}

      {offerWithCommissions &&
        !isFacilityFrame &&
        leasingCommissionsRead &&
        (!leasingCommissionsWrite && numberOfCommissions === 0 ? (
          <NoDataSummaryCard message={'OfferEdit.Commissions.Title'} />
        ) : (
          <CommissionsContainer documentId={documentId} siteId={siteId} numberOfCommissions={numberOfCommissions} />
        ))}

      {offerWithCommissions &&
        !isContractModification &&
        offerSubsidiesRead &&
        (!offerSubsidiesWrite && numberOfSubsidies === 0 ? (
          <NoDataSummaryCard message={'OfferEdit.Subsidies.Title'} />
        ) : (
          <SubsidiesContainer documentId={documentId} siteId={siteId} numberOfSubsidies={numberOfSubsidies} />
        ))}

      {!isContractModification && !isFacilityFrame && seasonalSettingsRead && (
        <OfferPaymentPlanParamsContainer documentId={documentId} siteId={siteId} />
      )}

      {hasVATFinancingDocument && <VatFinancingContainer documentId={documentId} siteId={siteId} />}
    </>
  );
};

OfferFinancialsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  actions: PropTypes.object.isRequired,
  currencyCode: PropTypes.string.isRequired,
  isContractModification: PropTypes.bool.isRequired,
  isFacilityFrame: PropTypes.bool
};

function mapStateToProps(state) {
  const offerTypeId = getOfferType(state);
  return {
    currencyCode: getCurrencyCode(state),
    partnerTypeCode: getOfferClientPartnerTypeCode(state),
    isContractModification: getIsContractModification(offerTypeId)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ loadFinancialDataSummary }, dispatch),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(OfferFinancialsContainer);
